import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';




export function CardHeader({ children, className = '' }: CardProps) {
  return (
    <div className={`p-6 ${className}`}>
      {children}
    </div>
  );
}

export function CardContent({ children, className = '' }: CardProps) {
  return (
    <div className={`p-6 pt-0 ${className}`}>
      {children}
    </div>
  );
}

export function CardTitle({ children, className = '' }: CardProps) {
  return (
    <h3 className={`text-2xl font-semibold ${className}`}>
      {children}
    </h3>
  );
}

export function CardDescription({ children, className = '' }: CardProps) {
  return (
    <p className={`text-gray-500 ${className}`}>
      {children}
    </p>
  );
}
interface ProgressProps {
  value: number;
  className?: string;
}

export function Progress({ value, className = '' }: ProgressProps) {
  return (
    <div className={`h-2 w-full bg-gray-200 rounded-full overflow-hidden ${className}`}>
      <div 
        className="h-full bg-orange-500 transition-all duration-300 ease-in-out"
        style={{ width: `${value}%` }}
      />
    </div>
  );
}
interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'secondary' | 'outline';
  className?: string;
}

export function Badge({ children, variant = 'default', className = '' }: BadgeProps) {
  const variants = {
    default: 'bg-orange-500 text-white',
    secondary: 'bg-gray-100 text-gray-900',
    outline: 'border border-gray-200 text-gray-900'
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
}
interface CardProps {
  children: React.ReactNode;
  className?: string;
}

export function Card({ children, className = '' }: CardProps) {
  return (
    <div className={`bg-white rounded-lg border shadow-sm ${className}`}>
      {children}
    </div>
  );
}
interface AstrologyType {
  id: string;
  label: string;
  category: string;
  practitioners: number;
  avgIncome: number;
  marketDemand: number;
  growth: number;
  description: string;
  timeToMaster: number;
}

interface ChartData {
  name: string;
  practitioners: number;
  income: number;
  marketDemand: number;
}

interface CustomChartProps {
  data: ChartData[];
}

interface TooltipData {
  x: number;
  y: number;
  value: number;
  type: string;
}

export function CustomChart({ data }: CustomChartProps) {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  
  const maxValue = Math.max(
    ...data.flatMap(d => [d.practitioners, d.income, d.marketDemand])
  );

  // Calculate dynamic widths based on number of items
  const barWidth = Math.max(10, Math.min(20, 100 / (data.length * 4))); // 4 = 3 bars + gap
  const maxHeight = Math.min(150, 600 / data.length);
  
  // Generate y-axis ticks
  const yAxisTicks = Array.from({ length: 5 }, (_, i) => Math.round(maxValue * (i + 1) / 5));

  const handleBarHover = (
    e: React.MouseEvent<HTMLDivElement>,
    value: number,
    type: string
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      value,
      type
    });
  };

  return (
    <div className="w-full h-[250px] relative flex items-end pl-12 pr-4 pb-12">
      {/* Y-axis */}
      <div className="absolute left-0 top-0 bottom-12 w-10 flex flex-col justify-center gap-10">
        {yAxisTicks.reverse().map((tick, i) => (
          <div key={i} className="flex items-center w-full">
            <span className="text-xs text-gray-500 mr-2">{tick}</span>
            <div className="flex-grow h-[1px] bg-gray-200" />
          </div>
        ))}
      </div>

      {/* X-axis */}
      <div className="absolute bottom-0 left-12 right-4 h-10 ">
        <div className="w-full h-[1px] bg-gray-300" />
      </div>

      {/* Bars */}
      <div className="w-full h-[200px] relative flex items-end justify-around border-l-2 pb-0">
        {data.map((item, index) => (
          <div key={index} className="flex flex-col items-center " style={{ width: `${100 / data.length}%` }}>
            <div className="flex gap-1 h-full items-end justify-center w-full  mb-0">
              <div 
                className="bg-[#f59e0b] rounded-t cursor-pointer transition-all hover:brightness-90"
                style={{ 
                  width: `${barWidth}%`,
                  height: `${(item.practitioners / maxValue) * maxHeight}px` 
                }}
                onMouseEnter={(e) => handleBarHover(e, item.practitioners, 'Practitioners')}
                onMouseLeave={() => setTooltip(null)}
              />
              <div 
                className="bg-[#ff7700] rounded-t cursor-pointer transition-all hover:brightness-90"
                style={{ 
                  width: `${barWidth}%`,
                  height: `${(item.income / maxValue) * maxHeight}px` 
                }}
                onMouseEnter={(e) => handleBarHover(e, item.income, 'Income')}
                onMouseLeave={() => setTooltip(null)}
              />
              <div 
                className="bg-[#ef4444] rounded-t cursor-pointer transition-all hover:brightness-90"
                style={{ 
                  width: `${barWidth}%`,
                  height: `${(item.marketDemand / maxValue) * maxHeight}px` 
                }}
                onMouseEnter={(e) => handleBarHover(e, item.marketDemand, 'Market Demand')}
                onMouseLeave={() => setTooltip(null)}
              />
            </div>
            <span className="absolute -bottom-16 text-sm max-sm:text-[9px] max-sm:-rotate-45 origin-top-left whitespace-nowrap h-fit">
              {item.name}
            </span>
          </div>
        ))}

        {/* Tooltip */}
        {tooltip && (
          <div 
            className="absolute bg-white shadow-lg rounded-lg p-2 text-sm pointer-events-none z-10 transform -translate-x-1/2 -translate-y-full"
            style={{ 
              left: `${tooltip.x}px`,
              top: `${tooltip.y - 10}px`
            }}
          >
            <div className="font-medium">{tooltip.type}</div>
            <div>{tooltip.value.toLocaleString()}</div>
          </div>
        )}
      </div>
    </div>
  );
}
export default function App() {
  const astrologyTypes: AstrologyType[] = [
    {
      id: "vedic",
      label: "Vedic Astrology",
      category: "Classical",
      practitioners: 25000,
      avgIncome: 75000,
      marketDemand: 85,
      growth: 12,
      description: "The traditional system of Hindu astrology (Jyotish) based on the Vedic texts",
      timeToMaster: 24,
    },
    {
      id: "kp",
      label: "KP Astrology",
      category: "Modern",
      practitioners: 15000,
      avgIncome: 82000,
      marketDemand: 88,
      growth: 15,
      description: "Krishnamurti Paddhati - A precise predictive system combining Vedic and Western techniques",
      timeToMaster: 18,
    },
    {
      id: "bnn",
      label: "BNN Astrology",
      category: "Ancient",
      practitioners: 8000,
      avgIncome: 95000,
      marketDemand: 92,
      growth: 18,
      description: "Ancient palm-leaf based predictive astrology system",
      timeToMaster: 36,
    },
    {
      id: "vaastu",
      label: "Vaastu Shastra",
      category: "Ancient",
      practitioners: 27000,
      avgIncome: 105000,
      marketDemand: 96,
      growth: 22.5,
      description: "Vaastu Shastra - The science of architecture and design",
      timeToMaster: 32,
    },
  ];

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [totalOpportunityScore, setTotalOpportunityScore] = useState(0);
  const [potentialEarnings, setPotentialEarnings] = useState(0);

  const handleTypeToggle = (typeId: string) => {
    const newSelected = selectedTypes.includes(typeId)
      ? selectedTypes.filter((id) => id !== typeId)
      : [...selectedTypes, typeId];

    setSelectedTypes(newSelected);

    const baseScore = newSelected.length * 25;
    setTotalOpportunityScore(Math.min(baseScore, 100));

    const selectedPractices = astrologyTypes.filter((type) =>
      newSelected.includes(type.id)
    );
    const baseEarnings = selectedPractices.reduce(
      (sum, practice) => sum + practice.avgIncome,
      0
    );
    setPotentialEarnings(Math.round(baseEarnings));
  };

  const selectedData = astrologyTypes.filter((type) =>
    selectedTypes.includes(type.id)
  );

  const chartData = selectedData.map((type) => ({
    name: type.label,
    practitioners: type.practitioners / 1000,
    income: type.avgIncome / 1000,
    marketDemand: type.marketDemand,
  }));

  return (
    <div className="w-full p-1 space-y-1">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 max-sm:justify-center max-sm:gap-2">
            <div>
              <CardTitle className="text-3xl max-sm:text-xl text-orange-500">
                Predict and Grow your Income with Vastu
              </CardTitle>
              <CardDescription className="text-[16px] mt-2">
                Check out the insights below curated for you to enable you to decide about your career in Vaastu Shastra.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2  max-sm:place-self-start">
              <div className="flex items-center gap-1 text-sm font-medium ">
                <span className="flex gap-1">
                  Potential Monthly Earnings: ₹{potentialEarnings.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center sm:justify-start gap-2">
                <div className="text-left max-sm:inline-flex items-center gap-0">
                  <div className="text-sm text-gray-500">Mastery Level</div>
                  <div className="flex items-center gap-2">
                    <Progress value={totalOpportunityScore} className="w-32" />
                    <span className="text-sm font-medium">{totalOpportunityScore}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <div className="flex max-sm:grid max-sm:grid-cols-2 gap-2 flex-wrap justify-center items-center">
              {astrologyTypes.map((type) => (
                <div key={type.id} className="sm:max-w-[30%]">
                  <div>
                    <div
                      className={`relative flex items-center space-y-1 rounded-lg border p-1 gap-1 transition-all duration-200 ${
                        selectedTypes.includes(type.id) ? "border-orange-500" : "border"
                      }`}
                      onClick={() => handleTypeToggle(type.id)}
                    >
                      <div className="top-1.5 right-1.5 flex items-center">
                        <input
                          id={type.id}
                          checked={selectedTypes.includes(type.id)}
                          onChange={() => handleTypeToggle(type.id)}
                          type="checkbox"
                          className="text-orange-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="text-center flex items-center justify-center gap-2">
                        <h3 className="text-sm font-medium">{type.label}</h3>
                      </div>
                      {selectedTypes.includes(type.id) && (
                        <Sparkles className="w-4 h-4 text-orange-500 absolute -top-2 -right-1" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Card className="w-full mt-4">
              <CardHeader>
                <CardTitle className="text-[17px]">Market Insights</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedTypes.length > 0 ? (
                  <CustomChart data={chartData} />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Please Select an Astrology Career type
                  </div>
                )}
                <div className="flex items-center justify-center gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#f59e0b]" />
                    <span className="text-sm">Practitioners (K)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#ff7700]" />
                    <span className="text-sm">Income (K₹)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#ef4444]" />
                    <span className="text-sm">Demand Score</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}